import { ChType, HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap, MapLocKeys } from '../../models';
import { LocDisplayType } from '../../models/display-type';
import { SYSTEM_ENUM_ROOT, SYSTEM_ENUM_TYPE } from '../shared-data';

const dpRoot = `${SYSTEM_ENUM_ROOT}|Credential History Type`;

type SharedKeys = Pick<
	LocDef,
	'portal' | 'displayType' | 'htmlAllowed' | 'portal' | 'shared' | 'feature'
>;

const sharedLocData: Pick<LocDef, 'displayType' | 'htmlAllowed' | 'shared' | 'feature'> = {
	displayType: `${SYSTEM_ENUM_TYPE}` as LocDisplayType,
	htmlAllowed: false,
	shared: true,
	feature: HubFeature.Credential,
};

const sharedSpLocData: SharedKeys = {
	...sharedLocData,
	portal: 'SP',
};

const sharedCpLocData: SharedKeys = {
	...sharedLocData,
	portal: 'CP',
};

export type CPChTypeLocs =
	| 'CPChTypeEnumInitialCert'
	| 'CPChTypeEnumLinkedExtension'
	| 'CPChTypeEnumLinkedSuspendedExtension'
	| 'CPChTypeEnumManualCertAdjust'
	| 'CPChTypeEnumManualCertAdjustNle'
	| 'CPChTypeEnumManualCertCreate'
	| 'CPChTypeEnumManualCertCreateNle'
	| 'CPChTypeEnumManualCertExtend'
	| 'CPChTypeEnumManualCertExtendNle'
	| 'CPChTypeEnumManualCertReduce'
	| 'CPChTypeEnumManualCertReduceNle'
	| 'CPChTypeEnumNormalExtension'
	| 'CPChTypeEnumSuspendedExtension';

export type SPChTypeLocs =
	| 'SPChTypeEnumInitialCert'
	| 'SPChTypeEnumLinkedExtension'
	| 'SPChTypeEnumLinkedSuspendedExtension'
	| 'SPChTypeEnumManualCertAdjust'
	| 'SPChTypeEnumManualCertAdjustNle'
	| 'SPChTypeEnumManualCertCreate'
	| 'SPChTypeEnumManualCertCreateNle'
	| 'SPChTypeEnumManualCertExtend'
	| 'SPChTypeEnumManualCertExtendNle'
	| 'SPChTypeEnumManualCertReduce'
	| 'SPChTypeEnumManualCertReduceNle'
	| 'SPChTypeEnumNormalExtension'
	| 'SPChTypeEnumSuspendedExtension';

export const cpChTypeEnumLocs: LocDefs<CPChTypeLocs> = {
	CPChTypeEnumInitialCert: {
		defaultValue: 'Initial Credential',
		displayPath: `${dpRoot}|${ChType.InitialCert}`,
		description: `System value for '${ChType.InitialCert}' Credential History type`,
		legacyName: 'Credential/System.Enum.ChType.InitialCert',
		...sharedCpLocData,
	},
	CPChTypeEnumLinkedExtension: {
		defaultValue: 'Linked Extension',
		displayPath: `${dpRoot}|${ChType.LinkedExtension}`,
		description: `System value for '${ChType.LinkedExtension}' Credential History type`,
		legacyName: 'Credential/System.Enum.ChType.LinkedExtension',
		...sharedCpLocData,
	},
	CPChTypeEnumLinkedSuspendedExtension: {
		defaultValue: 'Linked Suspended Extension',
		displayPath: `${dpRoot}|${ChType.LinkedSuspendedExtension}`,
		description: `System value for '${ChType.LinkedSuspendedExtension}' Credential History type`,
		legacyName: 'Credential/System.Enum.ChType.LinkedSuspendedExtension',
		...sharedCpLocData,
	},
	CPChTypeEnumManualCertAdjust: {
		defaultValue: 'Manual Credential Adjustment',
		displayPath: `${dpRoot}|${ChType.ManualCertAdjust}`,
		description: `System value for '${ChType.ManualCertAdjust}' Credential History type`,
		legacyName: 'Credential/System.Enum.ChType.ManualCertAdjust',
		...sharedCpLocData,
	},
	CPChTypeEnumManualCertAdjustNle: {
		defaultValue: 'Manual Credential Adjustment (No Linked Extension)',
		displayPath: `${dpRoot}|${ChType.ManualCertAdjustNle}`,
		description: `System value for '${ChType.ManualCertAdjustNle}' Credential History type`,
		legacyName: 'Credential/System.Enum.ChType.ManualCertAdjustNle',
		...sharedCpLocData,
	},
	CPChTypeEnumManualCertCreate: {
		defaultValue: 'Manual Credential Creation',
		displayPath: `${dpRoot}|${ChType.ManualCertCreate}`,
		description: `System value for '${ChType.ManualCertCreate}' Credential History type`,
		legacyName: 'Credential/System.Enum.ChType.ManualCertCreate',
		...sharedCpLocData,
	},
	CPChTypeEnumManualCertCreateNle: {
		defaultValue: 'Manual Credential Creation (No Linked Extension)',
		displayPath: `${dpRoot}|${ChType.ManualCertCreateNle}`,
		description: `System value for '${ChType.ManualCertCreateNle}' Credential History type`,
		legacyName: 'Credential/System.Enum.ChType.ManualCertCreateNle',
		...sharedCpLocData,
	},
	CPChTypeEnumManualCertExtend: {
		defaultValue: 'Manual Credential Extension',
		displayPath: `${dpRoot}|${ChType.ManualCertExtend}`,
		description: `System value for '${ChType.ManualCertExtend}' Credential History type`,
		legacyName: 'Credential/System.Enum.ChType.ManualCertExtend',
		...sharedCpLocData,
	},
	CPChTypeEnumManualCertExtendNle: {
		defaultValue: 'Manual Credential Extension (No Linked Extension)',
		displayPath: `${dpRoot}|${ChType.ManualCertExtendNle}`,
		description: `System value for '${ChType.ManualCertExtendNle}' Credential History type`,
		legacyName: 'Credential/System.Enum.ChType.ManualCertExtendNle',
		...sharedCpLocData,
	},
	CPChTypeEnumManualCertReduce: {
		defaultValue: 'Manual Credential Reduction',
		displayPath: `${dpRoot}|${ChType.ManualCertReduce}`,
		description: `System value for '${ChType.ManualCertReduce}' Credential History type`,
		legacyName: 'Credential/System.Enum.ChType.ManualCertReduce',
		...sharedCpLocData,
	},
	CPChTypeEnumManualCertReduceNle: {
		defaultValue: 'Manual Credential Reduction (No Linked Extension)',
		displayPath: `${dpRoot}|${ChType.ManualCertReduceNle}`,
		description: `System value for '${ChType.ManualCertReduceNle}' Credential History type`,
		legacyName: 'Credential/System.Enum.ChType.ManualCertReduceNle',
		...sharedCpLocData,
	},
	CPChTypeEnumNormalExtension: {
		defaultValue: 'Normal Extension',
		displayPath: `${dpRoot}|${ChType.NormalExtension}`,
		description: `System value for '${ChType.NormalExtension}' Credential History type`,
		legacyName: 'Credential/System.Enum.ChType.NormalExtension',
		...sharedCpLocData,
	},
	CPChTypeEnumSuspendedExtension: {
		defaultValue: 'Suspended Extension',
		displayPath: `${dpRoot}|${ChType.SuspendedExtension}`,
		description: `System value for '${ChType.SuspendedExtension}' Credential History type`,
		legacyName: 'Credential/System.Enum.ChType.SuspendedExtension',
		...sharedCpLocData,
	},
};

export const spChTypeEnumLocs: LocDefs<SPChTypeLocs> = {
	SPChTypeEnumInitialCert: {
		defaultValue: 'Initial Credential',
		displayPath: `${dpRoot}|${ChType.InitialCert}`,
		description: `System value for '${ChType.InitialCert}' Credential History type`,
		legacyName: 'Credential/System.Enum.ChType.InitialCert',
		...sharedSpLocData,
	},
	SPChTypeEnumLinkedExtension: {
		defaultValue: 'Linked Extension',
		displayPath: `${dpRoot}|${ChType.LinkedExtension}`,
		description: `System value for '${ChType.LinkedExtension}' Credential History type`,
		legacyName: 'Credential/System.Enum.ChType.LinkedExtension',
		...sharedSpLocData,
	},
	SPChTypeEnumLinkedSuspendedExtension: {
		defaultValue: 'Linked Suspended Extension',
		displayPath: `${dpRoot}|${ChType.LinkedSuspendedExtension}`,
		description: `System value for '${ChType.LinkedSuspendedExtension}' Credential History type`,
		legacyName: 'Credential/System.Enum.ChType.LinkedSuspendedExtension',
		...sharedSpLocData,
	},
	SPChTypeEnumManualCertAdjust: {
		defaultValue: 'Manual Credential Adjustment',
		displayPath: `${dpRoot}|${ChType.ManualCertAdjust}`,
		description: `System value for '${ChType.ManualCertAdjust}' Credential History type`,
		legacyName: 'Credential/System.Enum.ChType.ManualCertAdjust',
		...sharedSpLocData,
	},
	SPChTypeEnumManualCertAdjustNle: {
		defaultValue: 'Manual Credential Adjustment (No Linked Extension)',
		displayPath: `${dpRoot}|${ChType.ManualCertAdjustNle}`,
		description: `System value for '${ChType.ManualCertAdjustNle}' Credential History type`,
		legacyName: 'Credential/System.Enum.ChType.ManualCertAdjustNle',
		...sharedSpLocData,
	},
	SPChTypeEnumManualCertCreate: {
		defaultValue: 'Manual Credential Creation',
		displayPath: `${dpRoot}|${ChType.ManualCertCreate}`,
		description: `System value for '${ChType.ManualCertCreate}' Credential History type`,
		legacyName: 'Credential/System.Enum.ChType.ManualCertCreate',
		...sharedSpLocData,
	},
	SPChTypeEnumManualCertCreateNle: {
		defaultValue: 'Manual Credential Creation (No Linked Extension)',
		displayPath: `${dpRoot}|${ChType.ManualCertCreateNle}`,
		description: `System value for '${ChType.ManualCertCreateNle}' Credential History type`,
		legacyName: 'Credential/System.Enum.ChType.ManualCertCreateNle',
		...sharedSpLocData,
	},
	SPChTypeEnumManualCertExtend: {
		defaultValue: 'Manual Credential Extension',
		displayPath: `${dpRoot}|${ChType.ManualCertExtend}`,
		description: `System value for '${ChType.ManualCertExtend}' Credential History type`,
		legacyName: 'Credential/System.Enum.ChType.ManualCertExtend',
		...sharedSpLocData,
	},
	SPChTypeEnumManualCertExtendNle: {
		defaultValue: 'Manual Credential Extension (No Linked Extension)',
		displayPath: `${dpRoot}|${ChType.ManualCertExtendNle}`,
		description: `System value for '${ChType.ManualCertExtendNle}' Credential History type`,
		legacyName: 'Credential/System.Enum.ChType.ManualCertExtendNle',
		...sharedSpLocData,
	},
	SPChTypeEnumManualCertReduce: {
		defaultValue: 'Manual Credential Reduction',
		displayPath: `${dpRoot}|${ChType.ManualCertReduce}`,
		description: `System value for '${ChType.ManualCertReduce}' Credential History type`,
		legacyName: 'Credential/System.Enum.ChType.ManualCertReduce',
		...sharedSpLocData,
	},
	SPChTypeEnumManualCertReduceNle: {
		defaultValue: 'Manual Credential Reduction (No Linked Extension)',
		displayPath: `${dpRoot}|${ChType.ManualCertReduceNle}`,
		description: `System value for '${ChType.ManualCertReduceNle}' Credential History type`,
		legacyName: 'Credential/System.Enum.ChType.ManualCertReduceNle',
		...sharedSpLocData,
	},
	SPChTypeEnumNormalExtension: {
		defaultValue: 'Normal Extension',
		displayPath: `${dpRoot}|${ChType.NormalExtension}`,
		description: `System value for '${ChType.NormalExtension}' Credential History type`,
		legacyName: 'Credential/System.Enum.ChType.NormalExtension',
		...sharedSpLocData,
	},
	SPChTypeEnumSuspendedExtension: {
		defaultValue: 'Suspended Extension',
		displayPath: `${dpRoot}|${ChType.SuspendedExtension}`,
		description: `System value for '${ChType.SuspendedExtension}' Credential History type`,
		legacyName: 'Credential/System.Enum.ChType.SuspendedExtension',
		...sharedSpLocData,
	},
};

export const CH_TYPE_ENUM_LOCS = MapLocKeys<CPChTypeLocs | SPChTypeLocs>({
	...cpChTypeEnumLocs,
	...spChTypeEnumLocs,
});

export type CPChTypeEnumLocType = LocMap<CPChTypeLocs>;
export type SPChTypeEnumLocType = LocMap<SPChTypeLocs>;

export const cpChTypeKeys: { [key in ChType]: keyof CPChTypeEnumLocType } = {
	[ChType.InitialCert]: CH_TYPE_ENUM_LOCS.CPChTypeEnumInitialCert,
	[ChType.LinkedExtension]: CH_TYPE_ENUM_LOCS.CPChTypeEnumLinkedExtension,
	[ChType.LinkedSuspendedExtension]: CH_TYPE_ENUM_LOCS.CPChTypeEnumLinkedSuspendedExtension,
	[ChType.ManualCertAdjust]: CH_TYPE_ENUM_LOCS.CPChTypeEnumManualCertAdjust,
	[ChType.ManualCertAdjustNle]: CH_TYPE_ENUM_LOCS.CPChTypeEnumManualCertAdjustNle,
	[ChType.ManualCertCreate]: CH_TYPE_ENUM_LOCS.CPChTypeEnumManualCertCreate,
	[ChType.ManualCertCreateNle]: CH_TYPE_ENUM_LOCS.CPChTypeEnumManualCertCreateNle,
	[ChType.ManualCertExtend]: CH_TYPE_ENUM_LOCS.CPChTypeEnumManualCertExtend,
	[ChType.ManualCertExtendNle]: CH_TYPE_ENUM_LOCS.CPChTypeEnumManualCertExtendNle,
	[ChType.ManualCertReduce]: CH_TYPE_ENUM_LOCS.CPChTypeEnumManualCertReduce,
	[ChType.ManualCertReduceNle]: CH_TYPE_ENUM_LOCS.CPChTypeEnumManualCertReduceNle,
	[ChType.NormalExtension]: CH_TYPE_ENUM_LOCS.CPChTypeEnumNormalExtension,
	[ChType.SuspendedExtension]: CH_TYPE_ENUM_LOCS.CPChTypeEnumSuspendedExtension,
};

export const spChTypeKeys: { [key in ChType]: keyof SPChTypeEnumLocType } = {
	[ChType.InitialCert]: CH_TYPE_ENUM_LOCS.SPChTypeEnumInitialCert,
	[ChType.LinkedExtension]: CH_TYPE_ENUM_LOCS.SPChTypeEnumLinkedExtension,
	[ChType.LinkedSuspendedExtension]: CH_TYPE_ENUM_LOCS.SPChTypeEnumLinkedSuspendedExtension,
	[ChType.ManualCertAdjust]: CH_TYPE_ENUM_LOCS.SPChTypeEnumManualCertAdjust,
	[ChType.ManualCertAdjustNle]: CH_TYPE_ENUM_LOCS.SPChTypeEnumManualCertAdjustNle,
	[ChType.ManualCertCreate]: CH_TYPE_ENUM_LOCS.SPChTypeEnumManualCertCreate,
	[ChType.ManualCertCreateNle]: CH_TYPE_ENUM_LOCS.SPChTypeEnumManualCertCreateNle,
	[ChType.ManualCertExtend]: CH_TYPE_ENUM_LOCS.SPChTypeEnumManualCertExtend,
	[ChType.ManualCertExtendNle]: CH_TYPE_ENUM_LOCS.SPChTypeEnumManualCertExtendNle,
	[ChType.ManualCertReduce]: CH_TYPE_ENUM_LOCS.SPChTypeEnumManualCertReduce,
	[ChType.ManualCertReduceNle]: CH_TYPE_ENUM_LOCS.SPChTypeEnumManualCertReduceNle,
	[ChType.NormalExtension]: CH_TYPE_ENUM_LOCS.SPChTypeEnumNormalExtension,
	[ChType.SuspendedExtension]: CH_TYPE_ENUM_LOCS.SPChTypeEnumSuspendedExtension,
};
